import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import QuoteForm from "@/components/QuoteForm";
import { COMPANY } from "@/data/site";

const Contact = () => {
  const { hash } = useLocation();
  const [data, setData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (hash === "#quote") {
      const el = document.getElementById("quote");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }
  }, [hash]);

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email || !data.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", data);
      toast.success("Message received — we’ll be in touch shortly. Thank you.");
      setData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="bg-crest-green text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-24">
          <p className="text-xs uppercase tracking-[0.22em] text-crest-gold">Get in touch</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">Let’s talk about your project.</h1>
          <p className="mt-6 max-w-2xl text-white/80 font-body">
            Drop us a message, give us a call or request a free written estimate. We respond to every enquiry within one working day.
          </p>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:py-20 grid md:grid-cols-3 gap-5">
        {[
          { icon: Phone, t: "Call us",    v: COMPANY.phone, href: COMPANY.phoneHref, hint: "Mon–Fri 08:30–17:30 · Saturday 08:30–13:00" },
          { icon: Mail,  t: "Email us",   v: COMPANY.email, href: COMPANY.emailHref, hint: "We reply within one working day" },
          { icon: MapPin,t: "Service area", v: COMPANY.area, href: null, hint: "Liverpool yard · regional crews" },
        ].map((c) => {
          const Inner = (
            <>
              <div className="h-12 w-12 grid place-items-center rounded-md bg-crest-green-100 text-crest-green">
                <c.icon className="h-5 w-5" />
              </div>
              <div className="mt-5 text-xs uppercase tracking-[0.18em] text-crest-gold font-medium">{c.t}</div>
              <div className="mt-2 font-display text-lg font-semibold text-crest-navy break-all">{c.v}</div>
              <div className="mt-1 text-sm text-muted-foreground font-body">{c.hint}</div>
            </>
          );
          return c.href ? (
            <a key={c.t} href={c.href} className="rounded-lg border border-border bg-white p-7 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`contact-card-${c.t.toLowerCase().replace(/\s+/g, "-")}`}>
              {Inner}
            </a>
          ) : (
            <div key={c.t} className="rounded-lg border border-border bg-white p-7" data-testid={`contact-card-${c.t.toLowerCase().replace(/\s+/g, "-")}`}>
              {Inner}
            </div>
          );
        })}
      </section>

      {/* CONTACT FORM + HOURS */}
      <section className="bg-crest-surface border-y border-border">
        <div className="mx-auto max-w-7xl px-6 py-20 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Send a message</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-crest-navy leading-tight">Drop us a line.</h2>
            <p className="mt-4 text-muted-foreground font-body max-w-md">
              For general enquiries, partnership requests or to schedule a free site survey.
            </p>
            <div className="mt-10 rounded-lg bg-white border border-border p-7">
              <div className="flex items-center gap-3 text-crest-navy">
                <Clock className="h-5 w-5 text-crest-green" />
                <h3 className="font-display text-base font-semibold">Office hours</h3>
              </div>
              <hr className="gold-rule mt-3" />
              <ul className="mt-4 space-y-2 text-sm font-body">
                {COMPANY.hours.map((h) => (
                  <li key={h.day} className="flex items-center justify-between border-b border-border last:border-0 py-1.5">
                    <span className="text-muted-foreground">{h.day}</span>
                    <span className="text-crest-navy font-medium">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 rounded-lg bg-white border border-border p-7 md:p-9" data-testid="contact-form">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Full name</label>
                <Input value={data.name} onChange={set("name")} placeholder="Jane Smith" data-testid="contact-name" className="mt-2 h-11" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Phone</label>
                <Input value={data.phone} onChange={set("phone")} placeholder="07XXX XXXXXX" data-testid="contact-phone" className="mt-2 h-11" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Email</label>
                <Input type="email" value={data.email} onChange={set("email")} placeholder="you@email.com" data-testid="contact-email" className="mt-2 h-11" required />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Subject</label>
                <Input value={data.subject} onChange={set("subject")} placeholder="What’s it regarding?" data-testid="contact-subject" className="mt-2 h-11" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-[0.14em] text-crest-navy/70 font-medium">Message</label>
                <Textarea value={data.message} onChange={set("message")} rows={5} placeholder="Tell us how we can help…" data-testid="contact-message" className="mt-2" required />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-crest-green px-7 py-3.5 text-sm font-semibold text-white hover:bg-crest-green-700 transition-colors duration-200 disabled:opacity-60 w-full md:w-auto"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Send message
            </button>
          </form>
        </div>
      </section>

      {/* QUOTE BLOCK */}
      <section id="quote" className="mx-auto max-w-5xl px-6 py-20 md:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Or jump straight in</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-crest-navy">Request a free estimate</h2>
        </div>
        <div className="mt-10">
          <QuoteForm />
        </div>
      </section>

      {/* MAP */}
      <section className="border-t border-border">
        <div className="aspect-[16/6] w-full">
          <iframe
            title="Cresttor Construction service area — North West England"
            src="https://www.google.com/maps?q=Liverpool,+UK&output=embed"
            className="w-full h-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-map"
          />
        </div>
      </section>
    </div>
  );
};

export default Contact;
